/**
 * Component: EventFilters
 * Search and filter controls for event list
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EVENT_STATUS_LABELS, type EventStatus } from '@/types/event.types';
import { Search } from 'lucide-react';

interface EventFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilter: EventStatus | 'all';
  onStatusFilterChange: (value: EventStatus | 'all') => void;
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange?: (from: string | undefined, to: string | undefined) => void;
}

export function EventFilters({
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
}: EventFiltersProps) {
  return (
    <div className={`mb-6 grid grid-cols-1 gap-4 ${onDateRangeChange ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
      {/* Search */}
      <div className="space-y-2">
        <Label htmlFor="search">Buscar</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="search"
            type="text"
            placeholder="Buscar por nombre, código o ubicación..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Status Filter */}
      <div className="space-y-2">
        <Label htmlFor="status-filter">Estado</Label>
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger id="status-filter">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="DRAFT">{EVENT_STATUS_LABELS.DRAFT}</SelectItem>
            <SelectItem value="OPEN">{EVENT_STATUS_LABELS.OPEN}</SelectItem>
            <SelectItem value="CLOSED">{EVENT_STATUS_LABELS.CLOSED}</SelectItem>
            <SelectItem value="COMPLETED">
              {EVENT_STATUS_LABELS.COMPLETED}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {onDateRangeChange && (
        <>
          <div className="space-y-2">
            <Label htmlFor="date-from">Inicio desde</Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom ?? ''}
              onChange={(e) => onDateRangeChange(e.target.value || undefined, dateTo)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date-to">Inicio hasta</Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo ?? ''}
              onChange={(e) => onDateRangeChange(dateFrom, e.target.value || undefined)}
            />
          </div>
        </>
      )}
    </div>
  );
}
